import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Eye, RotateCcw, Save, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { safeHtml } from "@/lib/sanitize";

const TEMPLATE_LABELS = {
    initial_invitation: "Initial invitation",
    followup_3d: "3-day follow-up",
    followup_7d: "7-day follow-up",
    followup_14d: "14-day follow-up",
};

const TEMPLATE_HINTS = {
    initial_invitation: "Sent when a recruiter clicks 'Invite to Simonara' from Candidate Search.",
    followup_3d: "Sent when the recruiter clicks 3d follow-up in the CRM. Used as fallback if the AI copilot fails.",
    followup_7d: "Sent on the 7d follow-up. Fallback if AI copilot fails.",
    followup_14d: "Final 14d nudge. Fallback if AI copilot fails.",
};

function relativeTime(iso) {
    if (!iso) return "never";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 5) return "just now";
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

export default function RecruitEmailTemplates() {
    const [items, setItems] = useState([]);
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openKey, setOpenKey] = useState(null);
    const [drafts, setDrafts] = useState({}); // {key: {subject, body}}
    const [saving, setSaving] = useState(null);
    const [preview, setPreview] = useState(null); // {key, subject, body}
    const [previewLoading, setPreviewLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/recruit/templates");
            setItems(data.items || []);
            setPlaceholders(data.placeholders || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const startEdit = (t) => {
        setOpenKey(t.key);
        setDrafts((d) => ({ ...d, [t.key]: { subject: t.subject, body: t.body } }));
    };
    const setField = (key, field, value) => {
        setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: value } }));
    };
    const save = async (key) => {
        const draft = drafts[key];
        if (!draft?.subject?.trim() || !draft?.body?.trim()) {
            toast.error("Subject and body are both required.");
            return;
        }
        setSaving(key);
        try {
            await api.put(`/admin/recruit/templates/${key}`, draft);
            toast.success(`${TEMPLATE_LABELS[key]} saved.`);
            await load();
            setOpenKey(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(null); }
    };
    const reset = async (key) => {
        if (!window.confirm(`Reset "${TEMPLATE_LABELS[key]}" to the built-in default?`)) return;
        setSaving(key);
        try {
            await api.post(`/admin/recruit/templates/${key}/reset`);
            toast.success(`${TEMPLATE_LABELS[key]} reset to default.`);
            await load();
            setOpenKey(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(null); }
    };
    const showPreview = async (key) => {
        setPreviewLoading(true);
        const draft = drafts[key];
        try {
            const { data } = await api.post(`/admin/recruit/templates/${key}/preview`,
                draft ? { subject: draft.subject, body: draft.body } : {});
            setPreview({ key, subject: data.subject, body: data.body });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPreviewLoading(false); }
    };

    if (loading) return <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-violet-500" /></div>;

    return (
        <div className="card-soft p-4" data-testid="settings-email-templates">
            <div className="flex items-center gap-2 mb-2">
                <Mail size={14} className="text-violet-500" />
                <h3 className="font-heading font-medium">Email templates</h3>
            </div>
            <div className="mb-3 text-[11px] text-muted-foreground flex flex-wrap items-center gap-1.5">
                <Info size={11} />
                <span>Placeholders:</span>
                {placeholders.map((p) => (
                    <code key={p} className="px-1.5 py-0.5 rounded bg-muted text-[10px]" data-testid={`tpl-ph-${p}`}>{"{{" + p + "}}"}</code>
                ))}
            </div>
            <div className="divide-y divide-border">
                {items.map((t) => (
                    <div key={t.key} className="py-3" data-testid={`tpl-row-${t.key}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-medium text-sm">{TEMPLATE_LABELS[t.key] || t.key}</div>
                                <div className="text-xs text-muted-foreground">{TEMPLATE_HINTS[t.key] || ""}</div>
                                <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2" data-testid={`tpl-usage-${t.key}`}>
                                    <span>
                                        Used in <span className="font-medium text-foreground/80">{t.usage_count}</span> invitation{t.usage_count === 1 ? "" : "s"}
                                    </span>
                                    <span aria-hidden="true">·</span>
                                    <span>last sent {relativeTime(t.last_used_at)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {t.is_customized ? (
                                    <Badge variant="outline" className="border-violet-500/40 text-violet-700 dark:text-violet-400" data-testid={`tpl-badge-${t.key}`}>Customized</Badge>
                                ) : (
                                    <Badge variant="outline" className="text-muted-foreground" data-testid={`tpl-badge-${t.key}`}>Default</Badge>
                                )}
                                <Button size="sm" variant="outline" onClick={() => (openKey === t.key ? setOpenKey(null) : startEdit(t))} data-testid={`tpl-edit-${t.key}`}>
                                    {openKey === t.key ? "Close" : "Edit"}
                                </Button>
                            </div>
                        </div>
                        {openKey === t.key && (
                            <div className="mt-3 space-y-2" data-testid={`tpl-editor-${t.key}`}>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Subject</label>
                                    <input
                                        type="text"
                                        value={drafts[t.key]?.subject || ""}
                                        onChange={(e) => setField(t.key, "subject", e.target.value)}
                                        className="w-full mt-1 px-2.5 py-1.5 text-sm bg-background border border-border rounded-md focus:border-violet-500 focus:outline-none"
                                        data-testid={`tpl-subject-${t.key}`}
                                        maxLength={200}
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Body (HTML)</label>
                                    <textarea
                                        value={drafts[t.key]?.body || ""}
                                        onChange={(e) => setField(t.key, "body", e.target.value)}
                                        rows={10}
                                        className="w-full mt-1 px-2.5 py-1.5 text-xs font-mono bg-background border border-border rounded-md focus:border-violet-500 focus:outline-none"
                                        data-testid={`tpl-body-${t.key}`}
                                        maxLength={20000}
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <Button size="sm" onClick={() => save(t.key)} disabled={saving === t.key} data-testid={`tpl-save-${t.key}`}>
                                        {saving === t.key ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
                                        Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => showPreview(t.key)} disabled={previewLoading} data-testid={`tpl-preview-${t.key}`}>
                                        <Eye size={12} className="mr-1.5" /> Preview
                                    </Button>
                                    {t.is_customized && (
                                        <Button size="sm" variant="outline" onClick={() => reset(t.key)} disabled={saving === t.key} data-testid={`tpl-reset-${t.key}`}>
                                            <RotateCcw size={12} className="mr-1.5" /> Reset to default
                                        </Button>
                                    )}
                                    {t.updated_at && (
                                        <span className="text-[11px] text-muted-foreground ml-auto" data-testid={`tpl-meta-${t.key}`}>
                                            Updated {new Date(t.updated_at).toLocaleString()} by {t.updated_by}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {preview && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                    data-testid="tpl-preview-modal"
                >
                    <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 border-b border-border flex items-center justify-between">
                            <div className="text-sm font-medium">Preview — {TEMPLATE_LABELS[preview.key]}</div>
                            <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="tpl-preview-close">Close</Button>
                        </div>
                        <div className="p-3 border-b border-border text-sm">
                            <div className="text-[11px] uppercase text-muted-foreground">Subject</div>
                            <div className="font-medium" data-testid="tpl-preview-subject">{preview.subject}</div>
                        </div>
                        <div className="p-3 overflow-auto" data-testid="tpl-preview-body" dangerouslySetInnerHTML={{ __html: safeHtml(preview.body) }} />
                    </div>
                </div>
            )}
        </div>
    );
}
